import '../Analytics/Analytics.scss';
import './OnboardingFunnel.scss';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

const DAY_OPTIONS = [7, 30, 90];

const OnboardingFunnel = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = Number(searchParams.get('days')) || 30;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/analytics/onboarding-funnel?days=${days}`);
      setData(data);
    } catch (err) {
      console.error('Error fetching onboarding funnel:', err);
      setError(
        err?.response?.status === 503
          ? 'PostHog is not configured on the server yet.'
          : 'Could not load the onboarding funnel.',
      );
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchFunnel();
  }, [fetchFunnel]);

  const setDays = (d) => setSearchParams({ days: String(d) });

  // Open the per-person log behind a number. `step` alone lists everyone who
  // reached it; step + dropped lists only the ones who never reached the next.
  const openLog = ({ step, dropped, bucket }) => {
    const params = new URLSearchParams({ days: String(days) });
    if (step) params.set('step', step);
    if (dropped) params.set('dropped', '1');
    if (bucket) params.set('bucket', bucket);
    navigate(`/onboarding-funnel/log?${params.toString()}`);
  };

  const stages = data?.stages || [];
  const coarse = data?.coarse || {};
  const topPeople = stages[0]?.people || 0;

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analytics">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/analytics')}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '8px 16px', color: '#fff', cursor: 'pointer', fontSize: 14 }}>
                Back
              </button>
              <h1 className="analytics__title" style={{ margin: 0 }}>Onboarding Funnel</h1>
              <div className="onboarding__daypicker">
                {DAY_OPTIONS.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDays(d)}
                    className={days === d ? 'is-active' : ''}>
                    {d}d
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="analytics__loading">Loading...</div>
            ) : error ? (
              <div className="analytics__loading" style={{ color: '#ff6b6b' }}>{error}</div>
            ) : (
              <>
                {/* Coarse, always-available summary (works from 2.0.31 data).
                    Every card opens the per-person log behind its number. */}
                <div className="onboarding__cards">
                  <button
                    type="button"
                    className="onboarding__card"
                    onClick={() => openLog({ bucket: 'signup_completed' })}>
                    <span className="onboarding__card-label">Reached photo step</span>
                    <span className="onboarding__card-value">{coarse.signup_completed ?? 0}</span>
                    <span className="onboarding__card-sub">completed signup credentials</span>
                    <span className="onboarding__card-cta">See who &rsaquo;</span>
                  </button>
                  <button
                    type="button"
                    className="onboarding__card"
                    onClick={() => openLog({ bucket: 'photo_added' })}>
                    <span className="onboarding__card-label">Added a photo</span>
                    <span className="onboarding__card-value">{coarse.photo_added ?? 0}</span>
                    <span className="onboarding__card-sub">cleared the mandatory gate</span>
                    <span className="onboarding__card-cta">See who &rsaquo;</span>
                  </button>
                  <button
                    type="button"
                    className="onboarding__card onboarding__card--warn"
                    onClick={() => openLog({ bucket: 'photo_dropoff' })}>
                    <span className="onboarding__card-label">Dropped at photo</span>
                    <span className="onboarding__card-value">
                      {coarse.photo_step_dropoff ?? 0}
                      {coarse.photo_step_dropoff_pct ? ` (${coarse.photo_step_dropoff_pct}%)` : ''}
                    </span>
                    <span className="onboarding__card-sub">saw the photo screen, left</span>
                    <span className="onboarding__card-cta">See who &rsaquo;</span>
                  </button>
                </div>

                {/* Full step-by-step funnel (populates once 2.0.32 has users) */}
                {data?.hasStepData ? (
                  <div className="analytics__table" style={{ maxWidth: 720 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>Users reached</th>
                          <th>Dropped from previous</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stages.map((s, i) => {
                          const widthPct = topPeople > 0 ? Math.round((s.people / topPeople) * 100) : 0;
                          const prev = stages[i - 1];
                          return (
                            <tr key={s.key}>
                              <td>
                                <div className="onboarding__step">
                                  <span className="onboarding__step-label">{s.label}</span>
                                  <div className="onboarding__bar">
                                    <div className="onboarding__bar-fill" style={{ width: `${widthPct}%` }} />
                                  </div>
                                </div>
                              </td>
                              <td>
                                <button
                                  className="onboarding__linkbtn"
                                  title={`Everyone who reached ${s.label}`}
                                  onClick={() => openLog({ step: s.key })}>
                                  {s.people}
                                </button>
                              </td>
                              <td>
                                {s.droppedFromPrev > 0 ? (
                                  <button
                                    className="onboarding__linkbtn onboarding__drop"
                                    title={`The ${s.droppedFromPrev} who reached ${prev.label} and stopped there`}
                                    onClick={() => openLog({ step: prev.key, dropped: true })}>
                                    -{s.droppedFromPrev} ({s.dropoffPct}%)
                                  </button>
                                ) : (
                                  <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="onboarding__pending">
                    No step-by-step data in this window. Step events only exist on 2.0.32 and up,
                    so a window with no 2.0.32 signups shows nothing here. Once those users land,
                    every signup screen (name → phone → verify phone → email → verify email →
                    personal info → password → photo, or straight to personal info → photo for
                    Apple/Google) appears with its own drop-off. The summary above works on older
                    versions too.
                    <button
                      className="onboarding__linkbtn"
                      style={{ display: 'block', marginTop: 12 }}
                      onClick={() => openLog({})}>
                      See everyone who touched onboarding &rsaquo;
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default OnboardingFunnel;
